/**
 * Story 5.4: Beta User Onboarding - Feedback Hook
 *
 * Hook to check if beta feedback is due and manage modal display
 * Checks on app load and shows modal if:
 * - User is beta user
 * - 7 days have passed since invite
 * - Feedback not yet submitted
 */

import { useState, useEffect } from 'react';

interface BetaFeedbackState {
  shouldShowModal: boolean;
  isLoading: boolean;
  error: string | null;
}

interface UseBetaFeedbackResult extends BetaFeedbackState {
  dismissModal: () => void;
}

export function useBetaFeedback(userId: string | null): UseBetaFeedbackResult {
  const [state, setState] = useState<BetaFeedbackState>({
    shouldShowModal: false,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    async function checkFeedbackDue() {
      if (!userId) {
        setState({
          shouldShowModal: false,
          isLoading: false,
          error: null
        });
        return;
      }

      try {
        const response = await fetch(`/api/v1/beta/${userId}/feedback-due`, {
          credentials: 'include'
        });

        if (!response.ok) {
          // If endpoint returns 404 or error, user is not a beta user
          setState({
            shouldShowModal: false,
            isLoading: false,
            error: null
          });
          return;
        }

        const data = await response.json();

        // Check if feedback modal was already dismissed in this session
        const dismissedKey = `beta_feedback_dismissed_${userId}`;
        const wasDismissed = sessionStorage.getItem(dismissedKey);

        setState({
          shouldShowModal: data.success && data.data.feedbackDue && !wasDismissed,
          isLoading: false,
          error: null
        });
      } catch (err) {
        console.error('Error checking beta feedback status:', err);
        setState({
          shouldShowModal: false,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }
    }

    checkFeedbackDue();
  }, [userId]);

  const dismissModal = () => {
    if (userId) {
      // Store dismissal in sessionStorage to prevent re-showing in same session
      const dismissedKey = `beta_feedback_dismissed_${userId}`;
      sessionStorage.setItem(dismissedKey, 'true');
    }

    setState(prev => ({
      ...prev,
      shouldShowModal: false
    }));
  };

  return {
    ...state,
    dismissModal
  };
}
